import React from 'react';

import { Rooms } from '@search/filter-enums/enums/Rooms';
import {
    yMapHintContainer, yMapHintDescription, yMapHintDescriptionLite, yMapHintInfo, yMapHintPreview,
    yMapHintTitle
} from '@search/ymap/src/hint';
import { formatFloor } from '@search/helpers/src/formatFloor';
import { formatPrice } from '@search/helpers/src/formatPrice';
import { formatArea, formatAreaUnitFor } from '@search/helpers/src/formatArea';
import { Currency } from '@search/filter-enums/enums/Currency';

import { OfferYMapPlacemarkTooltip } from '../placemark/Placemark';
import { OfferYMapFlatPlacemarkProps } from './FlatPlacemarkProps';

export function OfferYMapFlatPlacemark({
    id,
    price,
    coordinates,
    totalArea,
    rent,
    floorsTotal,
    floor,
    roomType,
    preview,
    url,
    isVisited,
    isActive,
    isFavorite,
    onClick,
    targetBlank = true,
    isHighlighted,
    zIndexes,
    dataTest,
    isNewBuilding
}: OfferYMapFlatPlacemarkProps) {
    const areaPriceStr = ! rent && totalArea ? `${formatPrice({
        value: (price.rubPrice ?? price.value) / totalArea.value,
        currency: price.rubPrice ? Currency.RUB : price.currency,
        displayCurrency: true,
        noFractional: true
    })} за ${formatAreaUnitFor(totalArea)}` : undefined;

    const rentStr = rent?.untilitesIncluded ? 'КУ включены' : undefined;

    const roomTypeStr = roomsMapping[roomType];

    const hintLayout = yMapHintContainer(`
        ${yMapHintPreview(preview)}
        ${yMapHintInfo(`
            ${roomTypeStr ? yMapHintTitle(roomTypeStr) : ''}
            ${yMapHintDescription(`
                ${totalArea ? formatArea(totalArea) : ''}
                ${floor !== undefined ? `, ${formatFloor({ floor, floorsTotal })}` : ''}
            `)}
            ${yMapHintDescriptionLite(rentStr ?? areaPriceStr ?? '')}
        `)}
    `);

    const priceStr = formatPrice({
        value: price.rubPrice ?? price.value,
        currency: price.rubPrice ? Currency.RUB : price.currency,
        inWords: true
    });

    return (<OfferYMapPlacemarkTooltip
        id={id}
        dataTest={dataTest}
        url={url}
        isVisited={isVisited}
        isActive={isActive}
        onClick={onClick}
        title={priceStr}
        theme={isNewBuilding ? 'green' : undefined}
        targetBlank={targetBlank}
        coordinates={{ y: coordinates.latitude, x: coordinates.longitude }}
        hintLayout={hintLayout}
        isHighlighted={isHighlighted}
        zIndexes={zIndexes}
        isFavorite={isFavorite}
    />);
}

const roomsMapping = {
    [Rooms.UNKNOWN]: null,
    [Rooms.STUDIO]: 'Студия',
    [Rooms.ROOM_1]: '1 комната',
    [Rooms.ROOM_2]: '2 комнаты',
    [Rooms.ROOM_3]: '3 комнаты',
    [Rooms.ROOM_4]: '4 комнаты',
    [Rooms.ROOM_5_AND_MORE]: '5+ комнат',
    [Rooms.OPEN_PLAN]: 'Своб. планировка'
} as const;
